import React, { useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';

// =====================================================================
// LifeAtKBT — a magazine-style photo + stats moment that replaces the
// older cinematic almanac. Sits between the hero and the rabbi’s letter
// to convey what KBT actually looks and feels like, day to day.
// =====================================================================

const PHOTOS = [
  {
    src: '/assets/images/kbt/night-seder.jpg',
    caption: 'Night seder · the long table fills by 8:30',
    speed: -60,
  },
  {
    src: '/assets/images/kbt/shiur-yisraeli.jpg',
    caption: 'A shiur with Rabbi Yisraeli',
    speed: 40,
  },
  {
    src: '/assets/images/kbt/siyum.jpg',
    caption: 'Siyum · the moment the cycle closes',
    speed: -90,
  },
  {
    src: '/assets/images/kbt/night-seder-sept.jpg',
    caption: 'Night Seder',
    speed: 70,
  },
];

const STATS = [
  { value: '1,000,000', unit: 'hours', label: 'Of Torah — our 10-year goal' },
  { value: '15+',       unit: 'shiurim', label: 'Active weekly across three rabbis' },
  { value: '6 AM',      unit: 'daily',  label: 'Doors open · daf yomi by 6:00' },
  { value: '1',         unit: 'kehilla', label: 'Built one chair at a time' },
];

const PULL_QUOTES = [
  {
    quote: '"KBT is rebuilding what a Beit Midrash means in our city — open, alive, every day of the week."',
    source: 'Rabbi Brander',
  },
  {
    quote: '"It’s the only place I’ve been where the kettle, the books, and the people are all on, all the time."',
    source: 'A regular',
  },
];

function PhotoTile({ photo, progress, sx }) {
  const y = useTransform(progress, [0, 1], [photo.speed, -photo.speed]);
  return (
    <motion.div style={{ y }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          background: 'var(--navy-700)',
          ...sx,
        }}
      >
        <Box
          component="img"
          src={photo.src}
          alt=""
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 700ms cubic-bezier(0.22,1,0.36,1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, transparent 0%, transparent 60%, rgba(7,6,26,0.75) 100%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: 14,
            bottom: 12,
            right: 14,
            fontFamily: 'var(--font-mono)',
            fontSize: 9.5,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.85)',
            lineHeight: 1.3,
          }}
        >
          {photo.caption}
        </Box>
      </Box>
    </motion.div>
  );
}

function StatCard({ stat, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10% 0%' }}
      transition={{ duration: 0.9, delay: 0.08 * index, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        sx={{
          py: 3,
          borderTop: '1px solid rgba(255,255,255,0.2)',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: 'clamp(40px, 4.8vw, 64px)',
            lineHeight: 0.95,
            letterSpacing: '-0.02em',
            color: '#fff',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {stat.value}
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--teal-300)',
              marginLeft: 10,
              verticalAlign: 'super',
            }}
          >
            {stat.unit}
          </span>
        </Box>
        <Box
          sx={{
            mt: 1,
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            fontSize: 15,
            color: 'rgba(255,255,255,0.78)',
            lineHeight: 1.4,
          }}
        >
          {stat.label}
        </Box>
      </Box>
    </motion.div>
  );
}

export default function LifeAtKBT() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Big background number/Hebrew watermark drift
  const watermarkY = useTransform(scrollYProgress, [0, 1], ['10%', '-25%']);

  return (
    <Box
      ref={ref}
      component="section"
      id="life"
      sx={{
        position: 'relative',
        background: 'linear-gradient(180deg, var(--navy-700) 0%, var(--navy-900) 100%)',
        color: '#fff',
        py: { xs: 12, md: 18 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}
    >
      {/* Floating Hebrew watermark */}
      <motion.div
        style={{
          position: 'absolute',
          right: '-6%',
          top: '0%',
          fontFamily: 'var(--font-hebrew)',
          fontSize: 'min(80vw, 1100px)',
          color: '#fff',
          opacity: 0.03,
          lineHeight: 0.8,
          pointerEvents: 'none',
          userSelect: 'none',
          y: watermarkY,
        }}
        aria-hidden
      >
        ק
      </motion.div>

      <Box sx={{ position: 'relative', maxWidth: 1440, mx: 'auto' }}>
        {/* Section header */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          spacing={3}
          sx={{ mb: { xs: 6, md: 10 } }}
        >
          <Box sx={{ maxWidth: 800 }}>
            <Box
              className="kicker"
              sx={{ color: 'var(--teal-300)', mb: 2 }}
            >
              Life at KBT
            </Box>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0%' }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 'clamp(40px, 6vw, 96px)',
                lineHeight: 0.94,
                letterSpacing: '-0.025em',
                color: '#fff',
              }}
            >
              A Beit Midrash,
              <br />
              <span style={{ fontWeight: 500, fontStyle: 'italic', color: 'var(--teal-300)' }}>
                in motion.
              </span>
            </motion.h2>
          </Box>

          <Box
            sx={{
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: 'clamp(17px, 1.6vw, 22px)',
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.45,
              maxWidth: 420,
            }}
          >
            Not a snapshot — a rhythm. New faces every week, regulars every day,
            and the kettle on the whole time.
          </Box>
        </Stack>

        {/* Photo mosaic + stats — asymmetric grid */}
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              md: 'repeat(12, 1fr)',
            },
            gridAutoRows: { xs: '180px', md: '160px' },
            mb: { xs: 8, md: 12 },
          }}
        >
          {/* Large feature photo (left) */}
          <Box sx={{ gridColumn: { xs: '1 / 3', md: '1 / 6' }, gridRow: { md: 'span 3' } }}>
            <PhotoTile photo={PHOTOS[0]} progress={scrollYProgress} />
          </Box>

          {/* Top-right: rabbi teaching */}
          <Box sx={{ gridColumn: { xs: '1 / 2', md: '6 / 10' }, gridRow: { md: 'span 2' } }}>
            <PhotoTile photo={PHOTOS[1]} progress={scrollYProgress} />
          </Box>

          {/* Pull-quote card (replaces a tile) */}
          <Box
            sx={{
              gridColumn: { xs: '2 / 3', md: '10 / 13' },
              gridRow: { md: 'span 2' },
              p: { xs: 2, md: 3 },
              background: 'rgba(212,178,107,0.08)',
              border: '1px solid rgba(212,178,107,0.35)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 'clamp(14px, 1.3vw, 18px)',
                lineHeight: 1.35,
                color: '#fff',
              }}
            >
              {PULL_QUOTES[0].quote}
            </Box>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9.5,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--gold-light)',
                mt: 2,
              }}
            >
              — {PULL_QUOTES[0].source}
            </Box>
          </Box>

          {/* Siyum dance photo */}
          <Box sx={{ gridColumn: { xs: '1 / 3', md: '6 / 10' }, gridRow: { md: 'span 1' } }}>
            <PhotoTile photo={PHOTOS[2]} progress={scrollYProgress} />
          </Box>

          {/* Smaller bottom-right photo */}
          <Box sx={{ gridColumn: { xs: '1 / 3', md: '10 / 13' }, gridRow: { md: 'span 1' } }}>
            <PhotoTile photo={PHOTOS[3]} progress={scrollYProgress} />
          </Box>
        </Box>

        {/* Stats row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: { xs: 0, md: 4 },
            mb: { xs: 8, md: 12 },
            rowGap: { xs: 0 },
          }}
        >
          {STATS.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} index={i} />
          ))}
        </Box>

        {/* Second pull quote — full-width epigraph */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-10% 0%' }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <Box
            sx={{
              borderTop: '1px solid rgba(255,255,255,0.18)',
              pt: { xs: 6, md: 8 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 2.4fr' },
              gap: { xs: 3, md: 8 },
              alignItems: 'baseline',
            }}
          >
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--teal-300)',
              }}
            >
              From the kehilla
            </Box>
            <Box
              sx={{
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 'clamp(22px, 2.8vw, 36px)',
                lineHeight: 1.3,
                color: '#fff',
                letterSpacing: '-0.005em',
              }}
            >
              {PULL_QUOTES[1].quote}
              <Box
                sx={{
                  mt: 1.5,
                  fontFamily: 'var(--font-mono)',
                  fontStyle: 'normal',
                  fontSize: 10.5,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--gold-light)',
                }}
              >
                — {PULL_QUOTES[1].source}
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}
